import { useRef, useState } from 'react';
import { apiRequest } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

type ProfileForm = {
  name: string;
  email: string;
  phone: string;
  paroisse: string;
  diocese_prefere: string;
  avatar: string;
};

type PasswordForm = {
  current_password: string;
  password: string;
  password_confirmation: string;
};

const emptyPasswordForm: PasswordForm = {
  current_password: '',
  password: '',
  password_confirmation: '',
};

export default function AdminProfile() {
  const { user, updateUser } = useAuth();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<ProfileForm>({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    paroisse: user?.paroisse ?? '',
    diocese_prefere: user?.diocese_prefere ?? '',
    avatar: user?.avatar ?? '',
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [passwordForm, setPasswordForm] = useState<PasswordForm>(emptyPasswordForm);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const setField = (field: keyof ProfileForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleAvatarChange = (files: FileList | null) => {
    if (!files?.[0]) return;
    const file = files[0];
    setAvatarFile(file);
    setForm((current) => ({ ...current, avatar: URL.createObjectURL(file) }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const body = new FormData();
      body.append('_method', 'PUT');
      body.append('name', form.name);
      body.append('email', form.email);
      if (form.phone) body.append('phone', form.phone);
      if (form.paroisse) body.append('paroisse', form.paroisse);
      if (form.diocese_prefere) body.append('diocese_prefere', form.diocese_prefere);
      if (avatarFile) body.append('avatar_file', avatarFile);

      const updatedUser = await apiRequest<NonNullable<typeof user>>('/user', {
        method: 'POST',
        body,
      });

      updateUser(updatedUser);
      setAvatarFile(null);
      setForm((current) => ({ ...current, avatar: updatedUser.avatar ?? current.avatar }));
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès.' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Impossible de mettre à jour le profil.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (passwordForm.password.length < 8) {
      setMessage({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères.' });
      return;
    }

    if (passwordForm.password !== passwordForm.password_confirmation) {
      setMessage({ type: 'error', text: 'La confirmation du mot de passe ne correspond pas.' });
      return;
    }

    setSavingPassword(true);
    setMessage(null);

    try {
      await apiRequest('/user/password', {
        method: 'PUT',
        body: JSON.stringify(passwordForm),
      });
      setPasswordForm(emptyPasswordForm);
      setMessage({ type: 'success', text: 'Mot de passe mis à jour avec succès.' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Impossible de modifier le mot de passe.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col items-center text-center">
          {form.avatar ? (
            <img src={form.avatar} alt={form.name} className="h-28 w-28 rounded-full object-cover border-4 border-gold/20" />
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-navy text-3xl font-bold text-gold">
              {(form.name || 'A').slice(0, 2).toUpperCase()}
            </div>
          )}
          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            className="mt-4 rounded-xl border border-cream-dark px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-cream"
          >
            Changer la photo
          </button>
          <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={(event) => handleAvatarChange(event.target.files)} />
          <h2 className="mt-5 text-xl font-bold text-navy">{form.name || 'Administrateur'}</h2>
          <p className="text-sm text-gray-500">Compte administrateur</p>
          <div className="mt-4 w-full rounded-2xl bg-cream px-4 py-3 text-left text-sm text-gray-600">
            <p className="font-semibold text-navy">Informations visibles</p>
            <p className="mt-2 truncate">{form.email || 'Email non renseigné'}</p>
            <p className="truncate">{form.phone || 'Téléphone non renseigné'}</p>
            <p className="truncate">{form.paroisse || 'Paroisse non renseignée'}</p>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-navy">Modifier le profil</h3>
          <p className="text-sm text-gray-500">Mettez à jour vos informations administrateur et votre photo.</p>
        </div>

        {message ? (
          <div className={`mb-4 rounded-2xl px-4 py-3 text-sm ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            {message.text}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Nom complet</label>
            <input value={form.name} onChange={(event) => setField('name', event.target.value)} className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Email</label>
            <input type="email" value={form.email} onChange={(event) => setField('email', event.target.value)} className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Téléphone</label>
            <input value={form.phone} onChange={(event) => setField('phone', event.target.value)} className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Paroisse</label>
            <input value={form.paroisse} onChange={(event) => setField('paroisse', event.target.value)} className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Diocèse préféré</label>
            <input value={form.diocese_prefere} onChange={(event) => setField('diocese_prefere', event.target.value)} className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="rounded-2xl bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-70"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>

        <div className="mt-8 border-t border-gray-100 pt-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-navy">Modifier le mot de passe</h3>
              <p className="text-sm text-gray-500">Sécurisez votre compte administrateur.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowPasswordFields((current) => !current)}
              className="rounded-xl border border-cream-dark px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-cream"
            >
              <i className={showPasswordFields ? 'ri-eye-off-line mr-2' : 'ri-eye-line mr-2'}></i>
              {showPasswordFields ? 'Masquer' : 'Afficher'}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <input
              type={showPasswordFields ? 'text' : 'password'}
              value={passwordForm.current_password}
              onChange={(event) => setPasswordForm((current) => ({ ...current, current_password: event.target.value }))}
              placeholder="Mot de passe actuel"
              className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30"
            />
            <input
              type={showPasswordFields ? 'text' : 'password'}
              value={passwordForm.password}
              onChange={(event) => setPasswordForm((current) => ({ ...current, password: event.target.value }))}
              placeholder="Nouveau mot de passe"
              className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30"
            />
            <input
              type={showPasswordFields ? 'text' : 'password'}
              value={passwordForm.password_confirmation}
              onChange={(event) => setPasswordForm((current) => ({ ...current, password_confirmation: event.target.value }))}
              placeholder="Confirmer"
              className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => void handlePasswordSubmit()}
              disabled={savingPassword}
              className="rounded-2xl bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-70"
            >
              {savingPassword ? 'Modification...' : 'Changer le mot de passe'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
